import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { getTasks, deleteTask, getImageUrl } from '@/utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Trash2, CheckSquare, Square, Plus, ArrowUp, ArrowDown, FileSpreadsheet, ExternalLink, Filter, X, Save, ChevronDown, Settings2, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { useAuth } from '@/contexts/AuthContext';
import { STATUS_LABELS, STATUS_COLORS, STATUS_ROW_COLORS, PIECE_TYPES, DESIGNERS } from '@/utils/constants';

// All available columns
const ALL_COLUMNS = [
  { key: 'select', label: '', fixed: true, w: '28px' },
  { key: 'product_image', label: 'Ref', fixed: true, w: '55px' },
  { key: 'internal_cad_no', label: 'Design No', sortable: true, filterable: true, w: '160px' },
  { key: 'style_no', label: 'Style No', sortable: true, filterable: true, w: '80px' },
  { key: 'party_cad_no', label: 'Party No', sortable: true, filterable: true, w: '80px' },
  { key: 'client_name', label: 'Client', sortable: true, filterable: true, w: '100px' },
  { key: 'ks_design_no', label: 'KS Design No', sortable: true, filterable: true, w: '110px' },
  { key: 'product_name', label: 'Product', sortable: true, filterable: true, w: '130px' },
  { key: 'piece_type', label: 'Type', sortable: true, filterable: true, w: '65px' },
  { key: 'design_status', label: 'Status', sortable: true, filterable: true, w: '100px' },
  { key: 'priority', label: 'Pri', sortable: true, filterable: true, w: '50px' },
  { key: 'designer_name', label: 'Designer', sortable: true, filterable: true, w: '80px' },
  { key: 'designer_hashtag', label: '#Tag', sortable: true, w: '50px' },
  { key: 'design_points', label: 'Pts', sortable: true, w: '45px' },
  { key: 'metal_info', label: 'Metal', sortable: false, w: '110px' },
  { key: 'net_weight', label: 'Net Wt', sortable: true, w: '65px' },
  { key: 'gross_weight', label: 'Gross Wt', sortable: true, w: '70px' },
  { key: 'stones_summary', label: 'Stones', sortable: false, w: '75px' },
  { key: 'size', label: 'Size', sortable: true, filterable: true, w: '50px' },
  { key: 'engraving', label: 'Engr', sortable: true, w: '70px' },
  { key: 'texture', label: 'Finish', sortable: true, w: '70px' },
  { key: 'special_instructions', label: 'Instructions', sortable: false, w: '150px' },
  { key: 'cad_points', label: 'CAD Pts', sortable: true, w: '50px' },
  { key: 'cad_image', label: 'CAD', w: '55px' },
  { key: 'cad_url', label: 'CAD URL', w: '80px' },
  { key: 'quantity', label: 'Qty', sortable: true, w: '45px' },
  { key: 'revision_suffix', label: 'Rev', sortable: true, w: '40px' },
  { key: 'updated_at', label: 'Updated', sortable: true, w: '85px' },
  { key: 'created_at', label: 'Created', sortable: true, w: '85px' },
  { key: 'actions', label: '', fixed: true, w: '28px' },
];

const DEFAULT_VISIBLE = ['select', 'product_image', 'internal_cad_no', 'style_no', 'party_cad_no', 'client_name', 'ks_design_no', 'product_name', 'piece_type', 'design_status', 'priority', 'designer_name', 'metal_info', 'net_weight', 'stones_summary', 'size', 'engraving', 'texture', 'special_instructions', 'cad_points', 'cad_image', 'updated_at', 'actions'];

const STORAGE_KEY = 'pd_tracker_column_presets';

const loadPresets = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};
const savePresets = (presets) => localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));

const AllTasksListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportColumns, setExportColumns] = useState(DEFAULT_VISIBLE.filter(c => c !== 'select' && c !== 'actions'));
  const [presets, setPresets] = useState(loadPresets());
  const [presetName, setPresetName] = useState('');
  const [activeFilterCol, setActiveFilterCol] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedRevisions, setExpandedRevisions] = useState(new Set());

  const toggleRevisionExpand = (taskId, e) => {
    e.stopPropagation();
    setExpandedRevisions(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const canDelete = ['admin', 'master_admin'].includes(user?.role);

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try { const res = await getTasks(); setTasks(res.data); }
    catch (error) { if (error.response?.status !== 401) toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  // Compute unique values per filterable column
  const filterOptions = useMemo(() => {
    const opts = {};
    ALL_COLUMNS.filter(c => c.filterable).forEach(col => {
      const vals = new Set();
      tasks.forEach(t => {
        let v = t[col.key];
        if (col.key === 'design_status') v = STATUS_LABELS[v] || v;
        if (col.key === 'priority') v = (v === 'super_rush' || v === 'urgent') ? 'RUSH' : 'Normal';
        if (v && String(v).trim()) vals.add(String(v).trim());
      });
      opts[col.key] = [...vals].sort();
    });
    return opts;
  }, [tasks]);

  // Filter + sort
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.ks_design_no?.toLowerCase().includes(q) ||
        t.style_no?.toLowerCase().includes(q) || t.internal_cad_no?.toLowerCase().includes(q) ||
        t.party_cad_no?.toLowerCase().includes(q) || t.client_name?.toLowerCase().includes(q) ||
        t.product_name?.toLowerCase().includes(q) || t.designer_name?.toLowerCase().includes(q) ||
        t.special_instructions?.toLowerCase().includes(q) || t.notes?.toLowerCase().includes(q)
      );
    }
    // Column filters
    for (const [colKey, filterValue] of Object.entries(columnFilters)) {
      if (!filterValue) continue;
      filtered = filtered.filter(t => {
        let v = t[colKey];
        if (colKey === 'design_status') v = STATUS_LABELS[v] || v;
        if (colKey === 'priority') v = (v === 'super_rush' || v === 'urgent') ? 'RUSH' : 'Normal';
        return String(v || '').toLowerCase().includes(filterValue.toLowerCase());
      });
    }
    // Sort
    filtered.sort((a, b) => {
      let av = a[sortField] || '', bv = b[sortField] || '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return filtered;
  }, [tasks, searchQuery, columnFilters, sortField, sortDir]);

  const handleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } };
  const handleRowClick = (id) => navigate(`/tasks?selected=${id}`);
  const handleSelectTask = (e, id) => { e.stopPropagation(); setSelectedTasks(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); };
  const handleSelectAll = () => setSelectedTasks(p => p.length === filteredTasks.length ? [] : filteredTasks.map(t => t.id));
  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedTasks.length} tasks?`)) return;
    let deleted = 0, failed = 0;
    for (const id of selectedTasks) {
      try {
        await deleteTask(id);
        deleted++;
      } catch (err) {
        failed++;
      }
    }
    if (deleted > 0) toast.success(`Deleted ${deleted} task(s)`);
    if (failed > 0) toast.error(`Failed to delete ${failed} task(s)`);
    setSelectedTasks([]);
    fetchTasks();
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) { toast.error('Enter preset name'); return; }
    const updated = { ...presets, [presetName.trim()]: exportColumns };
    setPresets(updated);
    savePresets(updated);
    toast.success(`Preset "${presetName}" saved`);
    setPresetName('');
  };
  const handleLoadPreset = (name) => {
    setExportColumns(presets[name]);
    toast.success(`Loaded preset "${name}"`);
  };
  const handleDeletePreset = (name) => {
    const updated = { ...presets };
    delete updated[name];
    setPresets(updated);
    savePresets(updated);
  };

  const handleExportExcel = async (withImages = true) => {
    setExporting(true);
    try {
      const taskIds = filteredTasks.map(t => t.id);
      toast.info(`Generating Excel for ${taskIds.length} task(s)${withImages ? ' with images' : ''}...`);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/export/tasks-excel`,
        {
          task_ids: taskIds,
          columns: exportColumns.join(','),
          include_images: withImages,
        },
        { responseType: 'blob', timeout: 120000 }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PD_Tracker_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${taskIds.length} task(s)`);
      setShowExportPreview(false);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed — try with fewer columns or no images');
    } finally {
      setExporting(false);
    }
  };

  const getCellValue = (t, col) => {
    switch (col.key) {
      case 'metal_info': return t.metal_type ? `${t.metal_karat || ''} ${t.metal_colour || ''} ${t.metal_type}`.trim() : '—';
      case 'stones_summary': return t.total_diamond_pieces ? `${t.total_diamond_pieces}pc/${t.total_diamond_weight_ct || 0}ct` : '—';
      case 'design_status': return STATUS_LABELS[t.design_status] || t.design_status || '—';
      case 'priority': return (t.priority === 'super_rush' || t.priority === 'urgent') ? 'RUSH' : 'Normal';
      case 'updated_at': return t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—';
      case 'created_at': return t.created_at ? new Date(t.created_at).toLocaleDateString() : '—';
      case 'net_weight': return t.net_weight ? `${t.net_weight}g` : '—';
      case 'gross_weight': return t.gross_weight ? `${t.gross_weight}g` : '—';
      default: return t[col.key] || '—';
    }
  };

  const { roots, childrenMap } = useMemo(() => {
    const taskMap = new Map();
    filteredTasks.forEach(t => taskMap.set(t.id, t));
    
    // Group all tasks in filteredTasks by their root lineage ID
    const lineageGroups = new Map();
    filteredTasks.forEach(t => {
      const rootId = t.parent_task_id || t.id;
      if (!lineageGroups.has(rootId)) {
        lineageGroups.set(rootId, []);
      }
      lineageGroups.get(rootId).push(t);
    });
    
    const roots = [];
    const childrenMap = new Map();
    const processedRoots = new Set();
    
    filteredTasks.forEach(t => {
      const rootId = t.parent_task_id || t.id;
      if (processedRoots.has(rootId)) return;
      
      const lineage = lineageGroups.get(rootId) || [];
      // Sort lineage by revision_suffix ascending so the original (0) is first and latest is last
      lineage.sort((a, b) => (a.revision_suffix || 0) - (b.revision_suffix || 0));
      
      if (lineage.length > 0) {
        const latest = lineage[lineage.length - 1];
        roots.push(latest);
        
        const previousRevisions = lineage.slice(0, lineage.length - 1);
        if (previousRevisions.length > 0) {
          childrenMap.set(latest.id, previousRevisions);
        }
      }
      
      processedRoots.add(rootId);
    });
    
    return { roots, childrenMap };
  }, [filteredTasks]);

  const activeColumns = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
  const activeFiltersCount = Object.values(columnFilters).filter(Boolean).length;

  const Img = ({ src, s = 24 }) => {
    if (!src) return <div className="bg-muted/30 rounded" style={{ width: s, height: s }} />;
    return <img src={getImageUrl(src)} alt="" className="rounded border object-cover" style={{ width: s, height: s }} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />;
  };

  const renderExpandedRow = (t, plClass = "px-4") => (
    <tr key={`${t.id}-expand`} className="bg-slate-50 border-b border-border">
      <td colSpan={activeColumns.length} className={`py-3 ${plClass}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-[11px]">
          <div><span className="text-muted-foreground">Design No:</span> <span className="font-semibold font-mono">{t.internal_cad_no || '—'}</span></div>
          <div><span className="text-muted-foreground">Style No:</span> <span className="font-mono">{t.style_no || '—'}</span></div>
          <div><span className="text-muted-foreground">Party CAD:</span> <span className="font-mono">{t.party_cad_no || '—'}</span></div>
          <div><span className="text-muted-foreground">Client:</span> <span className="font-semibold">{t.client_name || '—'}</span></div>
          <div><span className="text-muted-foreground">Designer:</span> {t.designer_name || '—'} {t.designer_hashtag && `(${t.designer_hashtag})`}</div>
          <div><span className="text-muted-foreground">Design Pts:</span> {t.design_points || '—'}</div>
          <div><span className="text-muted-foreground">Metal:</span> {t.metal_type ? `${t.metal_karat || ''} ${t.metal_colour || ''} ${t.metal_type}`.trim() : '—'}</div>
          <div><span className="text-muted-foreground">Size:</span> {t.size || '—'}</div>
          <div><span className="text-muted-foreground">Net Wt:</span> {t.net_weight ? `${t.net_weight}g` : '—'}</div>
          <div><span className="text-muted-foreground">Gross Wt:</span> {t.gross_weight ? `${t.gross_weight}g` : '—'}</div>
          <div><span className="text-muted-foreground">Priority:</span> {(t.priority === 'super_rush' || t.priority === 'urgent') ? '⚠️ SUPER RUSH' : 'Normal'}</div>
          <div><span className="text-muted-foreground">Rev:</span> {t.revision_suffix || '—'}</div>
          {t.cad_url && <div className="col-span-2"><span className="text-muted-foreground">CAD URL:</span> <span className="font-mono text-[10px]">{t.cad_url}</span></div>}
          {t.special_instructions && <div className="col-span-2"><span className="text-muted-foreground">Instructions:</span> {t.special_instructions}</div>}
          {t.notes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {t.notes}</div>}
        </div>
        <div className="flex items-center gap-3 mt-3 pt-2 border-t border-border/50">
          {t.product_image && <img src={getImageUrl(t.product_image)} alt="Product" className="w-16 h-16 rounded border object-cover" />}
          {t.cad_image && <img src={getImageUrl(t.cad_image)} alt="CAD" className="w-16 h-16 rounded border object-cover" />}
          <Button size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={() => navigate(`/tasks?selected=${t.id}`)} data-testid={`open-task-${t.id}`}><ExternalLink size={10} className="mr-1" />Open Full View</Button>
        </div>
      </td>
    </tr>
  );

  const renderTaskRow = (t, idx, isChild, hasChildren, isExpanded) => {
    return (
      <tr
        onClick={() => handleRowClick(t.id)}
        className={`cursor-pointer border-b border-border/20 transition-colors hover:brightness-95 ${
          selectedTasks.includes(t.id) ? 'ring-1 ring-inset ring-blue-400' : ''
        } ${isChild ? 'bg-slate-50/60 font-medium' : (STATUS_ROW_COLORS[t.design_status] || (idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'))}`}
        data-testid={`grid-row-${t.id}`}
      >
        {activeColumns.map(col => {
          switch (col.key) {
            case 'select':
              return (
                <td key={col.key} className="px-1 py-1" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center">
                    {isChild && <span className="w-3 flex-shrink-0" />}
                    <button onClick={e => handleSelectTask(e, t.id)}>
                      {selectedTasks.includes(t.id) ? <CheckSquare size={12} className="text-blue-600" /> : <Square size={12} className="text-muted-foreground" />}
                    </button>
                  </div>
                </td>
              );
            case 'product_image':
              return <td key={col.key} className="px-1 py-0.5"><Img src={t.product_image} s={48} /></td>;
            case 'cad_image':
              return <td key={col.key} className="px-1 py-0.5">{t.cad_image ? <Img src={t.cad_image} s={48} /> : <span className="text-muted-foreground text-[9px]">&mdash;</span>}</td>;
            case 'internal_cad_no':
              return (
                <td key={col.key} className="px-1.5 py-1 whitespace-nowrap truncate max-w-[200px]">
                  <div className="flex items-center gap-1.5">
                    {isChild && <span className="text-muted-foreground text-xs select-none mr-0.5 font-bold">↳</span>}
                    {hasChildren && (
                      <button
                        onClick={(e) => toggleRevisionExpand(t.id, e)}
                        className="p-0.5 hover:bg-muted rounded transition-transform duration-200"
                        title={isExpanded ? "Collapse revisions" : "Expand revisions"}
                      >
                        <ChevronDown size={12} className={`text-muted-foreground transform transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                      </button>
                    )}
                    <span className="font-bold font-mono text-[13px] md:text-[15px] tracking-wide text-foreground" title={String(getCellValue(t, col))}>
                      {getCellValue(t, col)}
                    </span>
                  </div>
                </td>
              );
            case 'design_status':
              return <td key={col.key} className="px-1.5 py-1"><span className={`inline-block px-1 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${STATUS_COLORS[t.design_status] || ''}`}>{STATUS_LABELS[t.design_status] || t.design_status}</span></td>;
            case 'priority':
              return <td key={col.key} className="px-1.5 py-1">{(t.priority === 'super_rush' || t.priority === 'urgent') ? <span className="flex items-center justify-center text-red-500" title="SUPER RUSH"><AlertTriangle size={14} className="fill-red-100 animate-pulse" /></span> : null}</td>;
            case 'actions':
              return <td key={col.key} className="px-1 py-1 text-center" onClick={e => e.stopPropagation()}><Button size="sm" variant="ghost" className={`h-5 w-5 p-0 ${expandedRow === t.id ? 'text-blue-600 bg-blue-50' : ''}`} onClick={() => setExpandedRow(expandedRow === t.id ? null : t.id)}><Eye size={11} /></Button></td>;
            default:
              return <td key={col.key} className="px-1.5 py-1 whitespace-nowrap truncate max-w-[130px]" title={String(getCellValue(t, col))}>{getCellValue(t, col)}</td>;
          }
        })}
      </tr>
    );
  };

  return (
    <CollapsibleLayout>
      <div className="flex flex-col h-[calc(100vh-48px)] bg-background" data-testid="all-tasks-grid-page">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-bold">All CAD Files</h1>
            <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">{filteredTasks.length} / {tasks.length}</span>
            {activeFiltersCount > 0 && (
              <button onClick={() => setColumnFilters({})} className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                <X size={10} />{activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active — clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowColumnPicker(!showColumnPicker)} className="h-7 text-xs" data-testid="column-picker-btn">
              <Settings2 size={13} className="mr-1" />Columns
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setExportColumns(visibleColumns.filter(c => c !== 'select' && c !== 'actions')); setShowExportPreview(true); }} className="h-7 text-xs" data-testid="export-excel-btn">
              <FileSpreadsheet size={13} className="mr-1" />Export
            </Button>
            <Button size="sm" onClick={() => navigate('/tasks/new')} className="h-7 text-xs bg-[hsl(220,60%,20%)] text-white hover:bg-[hsl(220,60%,25%)]" data-testid="create-new-btn">
              <Plus size={13} className="mr-1" />New CAD
            </Button>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border bg-muted/30 flex-shrink-0">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search style, client, instructions..." className="pl-8 h-7 text-xs" data-testid="grid-search-input" />
          </div>
          {selectedTasks.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-blue-600 font-medium">{selectedTasks.length} selected</span>
              {canDelete && <Button size="sm" variant="ghost" className="h-6 text-xs text-red-500" onClick={handleBulkDelete}><Trash2 size={12} className="mr-1" />Delete</Button>}
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setSelectedTasks([])}>Clear</Button>
            </div>
          )}
        </div>

        {/* Column Picker Dropdown */}
        {showColumnPicker && (
          <div className="absolute right-4 top-24 z-30 bg-card border border-border rounded-lg shadow-xl p-3 w-64 max-h-80 overflow-auto" data-testid="column-picker-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold">Visible Columns</span>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setShowColumnPicker(false)}><X size={12} /></Button>
            </div>
            <div className="space-y-1">
              {ALL_COLUMNS.filter(c => !c.fixed).map(col => (
                <label key={col.key} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 px-1 py-0.5 rounded">
                  <Checkbox
                    checked={visibleColumns.includes(col.key)}
                    onCheckedChange={(checked) => {
                      setVisibleColumns(prev => checked ? [...prev, col.key] : prev.filter(k => k !== col.key));
                    }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 text-[10px] flex-1" onClick={() => setVisibleColumns(DEFAULT_VISIBLE)}>Reset</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] flex-1" onClick={() => setVisibleColumns(ALL_COLUMNS.map(c => c.key))}>Show All</Button>
            </div>
          </div>
        )}

        {/* Main table */}
        <div className="flex-1 overflow-auto relative">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">Loading...</div>
          ) : filteredTasks.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">{searchQuery || activeFiltersCount ? 'No matches' : 'No tasks yet'}</div>
          ) : (
            <table className="w-full text-[13px] border-collapse" data-testid="tasks-grid-table">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: 'hsl(220, 60%, 20%)' }}>
                <tr>
                  {activeColumns.map(col => (
                    <th key={col.key} className="px-1.5 py-1.5 text-left text-[12px] font-bold uppercase tracking-wide text-white/90 whitespace-nowrap" style={{ minWidth: col.w }}>
                      {col.key === 'select' ? (
                        <button onClick={handleSelectAll} className="text-white/80 hover:text-white">{selectedTasks.length === filteredTasks.length && filteredTasks.length > 0 ? <CheckSquare size={12} /> : <Square size={12} />}</button>
                      ) : (
                        <div className="flex items-center gap-0.5">
                          {col.sortable ? (
                            <button onClick={() => handleSort(col.key)} className="flex items-center gap-0.5 hover:text-white">
                              {col.label}
                              {sortField === col.key ? (sortDir === 'asc' ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />) : null}
                            </button>
                          ) : <span>{col.label}</span>}
                          {col.filterable && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setActiveFilterCol(activeFilterCol === col.key ? null : col.key); }}
                              className={`ml-0.5 p-0.5 rounded hover:bg-white/20 ${columnFilters[col.key] ? 'text-yellow-300' : 'text-white/40'}`}
                              title={`Filter ${col.label}`}
                            >
                              <Filter size={9} />
                            </button>
                          )}
                        </div>
                      )}
                      {/* Column filter dropdown */}
                      {activeFilterCol === col.key && col.filterable && (
                        <div className="absolute mt-1 bg-card border border-border rounded-lg shadow-xl p-2 w-48 z-20 text-foreground font-normal" onClick={e => e.stopPropagation()}>
                          <Input
                            autoFocus
                            value={columnFilters[col.key] || ''}
                            onChange={e => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                            placeholder={`Filter ${col.label}...`}
                            className="h-6 text-xs mb-1"
                          />
                          <div className="max-h-32 overflow-auto space-y-0.5">
                            {(filterOptions[col.key] || []).slice(0, 20).map(val => (
                              <button
                                key={val}
                                onClick={() => { setColumnFilters(prev => ({ ...prev, [col.key]: val })); setActiveFilterCol(null); }}
                                className="block w-full text-left px-1.5 py-0.5 text-[10px] rounded hover:bg-muted/50 truncate"
                              >
                                {val}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-1 mt-1 pt-1 border-t">
                            <Button size="sm" variant="ghost" className="h-5 text-[9px] flex-1" onClick={() => { setColumnFilters(prev => ({ ...prev, [col.key]: '' })); setActiveFilterCol(null); }}>Clear</Button>
                            <Button size="sm" variant="ghost" className="h-5 text-[9px] flex-1" onClick={() => setActiveFilterCol(null)}>Close</Button>
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roots.map((t, idx) => {
                  const children = childrenMap.get(t.id) || [];
                  const hasChildren = children.length > 0;
                  const isExpanded = expandedRevisions.has(t.id);
                  
                  return (
                    <React.Fragment key={t.id}>
                      {renderTaskRow(t, idx, false, hasChildren, isExpanded)}
                      {expandedRow === t.id && renderExpandedRow(t, "px-4")}
                      
                      {hasChildren && isExpanded && children.map((child, childIdx) => (
                        <React.Fragment key={child.id}>
                          {renderTaskRow(child, childIdx, true, false, false)}
                          {expandedRow === child.id && renderExpandedRow(child, "pl-10")}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Export Preview Modal */}
        {showExportPreview && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="export-preview-modal">
            <div className="bg-card border border-border rounded-xl shadow-2xl w-[700px] max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <h3 className="text-sm font-bold">Export Preview</h3>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowExportPreview(false)}><X size={16} /></Button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                {/* Presets */}
                <div className="mb-4">
                  <p className="text-xs font-semibold mb-1">Presets</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {Object.keys(presets).map(name => (
                      <div key={name} className="flex items-center gap-1 bg-muted/50 rounded px-2 py-1">
                        <button onClick={() => handleLoadPreset(name)} className="text-[10px] font-medium text-primary hover:underline">{name}</button>
                        <button onClick={() => handleDeletePreset(name)} className="text-muted-foreground hover:text-destructive"><X size={10} /></button>
                      </div>
                    ))}
                    <div className="flex items-center gap-1">
                      <Input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="Preset name" className="h-6 text-[10px] w-28" />
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleSavePreset}><Save size={10} className="mr-0.5" />Save</Button>
                    </div>
                  </div>
                </div>

                {/* Column selection */}
                <p className="text-xs font-semibold mb-1">Select columns to export ({exportColumns.length} selected)</p>
                <div className="grid grid-cols-4 gap-1 mb-4">
                  {ALL_COLUMNS.filter(c => !c.fixed).map(col => (
                    <label key={col.key} className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:bg-muted/30 px-1 py-0.5 rounded">
                      <Checkbox
                        checked={exportColumns.includes(col.key)}
                        onCheckedChange={(checked) => {
                          setExportColumns(prev => checked ? [...prev, col.key] : prev.filter(k => k !== col.key));
                        }}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>

                {/* Preview table */}
                <p className="text-xs font-semibold mb-1">Preview (first 5 rows)</p>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-[9px] border-collapse">
                    <thead className="bg-muted/50">
                      <tr>{exportColumns.map(key => { const col = ALL_COLUMNS.find(c => c.key === key); return <th key={key} className="px-1.5 py-1 text-left font-semibold whitespace-nowrap">{col?.label || key}</th>; })}</tr>
                    </thead>
                    <tbody>
                      {filteredTasks.slice(0, 5).map(t => (
                        <tr key={t.id} className="border-t border-border/30">
                          {exportColumns.map(key => {
                            const col = ALL_COLUMNS.find(c => c.key === key);
                            return <td key={key} className="px-1.5 py-0.5 whitespace-nowrap truncate max-w-[100px]">{col ? getCellValue(t, col) : (t[key] || '—')}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Will export {filteredTasks.length} rows</p>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
                <Button variant="ghost" size="sm" onClick={() => setShowExportPreview(false)} className="h-8 text-xs">Cancel</Button>
                <Button size="sm" variant="outline" onClick={() => handleExportExcel(false)} disabled={exporting} className="h-8 text-xs" data-testid="export-no-images-btn">
                  {exporting ? <Loader2 size={13} className="mr-1 animate-spin" /> : <FileSpreadsheet size={13} className="mr-1" />}
                  Export (No Images)
                </Button>
                <Button size="sm" onClick={() => handleExportExcel(true)} disabled={exporting} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-export-btn">
                  {exporting ? <Loader2 size={13} className="mr-1 animate-spin" /> : <FileSpreadsheet size={13} className="mr-1" />}
                  Export with Images
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleLayout>
  );
};

export default AllTasksListPage;
